export interface SanitizationOptions {
  maxLength?: number
  allowHtml?: boolean
  allowUrls?: boolean
  trimWhitespace?: boolean
  stripScripts?: boolean
  stripInlineEvents?: boolean
  stripDangerousTags?: boolean
}

export const sanitizeInput = (input: string, options: SanitizationOptions = {}): string => {
  if (typeof input !== 'string') return ''

  const { 
    maxLength = 1000, 
    allowHtml = false, 
    allowUrls = true, 
    trimWhitespace = true,
    stripScripts = true,
    stripInlineEvents = true,
    stripDangerousTags = true
  } = options

  let sanitized = input

  // Trim whitespace if enabled
  if (trimWhitespace) {
    sanitized = sanitized.trim()
  }

  // Remove dangerous script tags and inline scripts
  if (stripScripts) {
    sanitized = sanitized
      .replace(/<script[^>]*>.*?<\/script>/gis, '')
      .replace(/javascript:/gi, '')
      .replace(/vbscript:/gi, '')
      .replace(/data:text\/html/gi, '')
      .replace(/onload\s*=/gi, '')
      .replace(/onerror\s*=/gi, '')
  }

  // Remove inline event handlers
  if (stripInlineEvents) {
    sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
  }

  // Remove dangerous HTML tags
  if (stripDangerousTags) {
    const dangerousTags = /<(script|iframe|object|embed|link|meta|style|form|input|button)[^>]*>.*?<\/\1>/gis
    sanitized = sanitized.replace(dangerousTags, '')
  }

  // Remove HTML tags if not allowed
  if (!allowHtml) {
    sanitized = sanitized.replace(/<[^>]*>/g, '')
  }

  // Remove potentially dangerous characters and sequences
  sanitized = sanitized
    .replace(/[<>'"&]/g, (match) => {
      const entities: { [key: string]: string } = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '&': '&amp;',
      }
      return entities[match] || match
    })
    // Remove null bytes and control characters
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    // Remove excessive whitespace
    .replace(/\s+/g, ' ')

  // Handle URLs
  if (!allowUrls) {
    // Remove URLs (basic pattern)
    sanitized = sanitized.replace(/https?:\/\/[^\s]+/gi, '')
  } else {
    // Validate URLs if they exist
    const urlPattern = /(https?:\/\/[^\s]+)/gi
    sanitized = sanitized.replace(urlPattern, (url) => {
      try {
        new URL(url)
        return url
      } catch {
        return ''
      }
    })
  }

  // Enforce maximum length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength)
  }

  return sanitized
}

export const sanitizeEmail = (email: string): string => {
  return sanitizeInput(email, {
    maxLength: 254,
    allowHtml: false,
    allowUrls: false,
    trimWhitespace: true,
  })
}

export const sanitizeUrl = (url: string): string => {
  const sanitized = sanitizeInput(url, {
    maxLength: 2048,
    allowHtml: false,
    allowUrls: true,
    trimWhitespace: true,
  })

  // Additional URL validation
  try {
    const validatedUrl = new URL(sanitized)
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(validatedUrl.protocol)) {
      return ''
    }
    return validatedUrl.toString()
  } catch {
    return ''
  }
}

export const sanitizeText = (text: string, maxLength: number = 500): string => {
  return sanitizeInput(text, {
    maxLength,
    allowHtml: false,
    allowUrls: false,
    trimWhitespace: true,
  })
}

export const sanitizeDescription = (description: string): string => {
  return sanitizeInput(description, {
    maxLength: 5000,
    allowHtml: false,
    allowUrls: true,
    trimWhitespace: true,
  })
}

export const sanitizeName = (name: string): string => {
  return sanitizeInput(name, {
    maxLength: 100,
    allowHtml: false,
    allowUrls: false,
    trimWhitespace: true,
  })
}

export const sanitizePhone = (phone: string): string => {
  const sanitized = sanitizeInput(phone, {
    maxLength: 20,
    allowHtml: false,
    allowUrls: false,
    trimWhitespace: true,
  })
  
  // Only allow digits, spaces, dashes, parentheses, and plus sign
  return sanitized.replace(/[^0-9\s\-\(\)\+]/g, '')
}

export const sanitizeJobTitle = (title: string): string => {
  return sanitizeInput(title, {
    maxLength: 200,
    allowHtml: false,
    allowUrls: false,
    trimWhitespace: true,
  })
}

export const sanitizeCompanyName = (company: string): string => {
  return sanitizeInput(company, {
    maxLength: 100,
    allowHtml: false,
    allowUrls: false,
    trimWhitespace: true,
  })
}

export const sanitizeMessage = (message: string): string => {
  return sanitizeInput(message, {
    maxLength: 2000,
    allowHtml: false,
    allowUrls: false,
    trimWhitespace: true,
  })
}

export const sanitizeLocation = (location: string): string => {
  return sanitizeInput(location, {
    maxLength: 200,
    allowHtml: false,
    allowUrls: false,
    trimWhitespace: true,
  })
}

export const sanitizeBio = (bio: string): string => {
  return sanitizeInput(bio, {
    maxLength: 1000,
    allowHtml: false,
    allowUrls: true,
    trimWhitespace: true,
  })
}
